/* eslint-disable class-methods-use-this */
import { Language } from '@nx-smartmonkey/shared/domain';
import { Requests, arrayBufferToImageSrcBase64 } from '@nx-smartmonkey/shared/helpers';
import { Organization } from '../../domain/organization/Organization';
import { OrganizationDTO } from '../../domain/organization/OrganizationDTO';
import { OrganizationUser } from '../../domain/organization/OrganizationUser';
import { OrganizationUserDTO } from '../../domain/organization/OrganizationUserDTO';
import { OrganizationResources } from '../../domain/resources/OrganizationResources';
import { OrganizationResourcesDTO } from '../../domain/resources/OrganizationResourcesDTO';
import { BillingPeriod, PlanType } from '../../domain/subscription/Subscription';
import { OrganizationMapper } from '../../mappers/organization/OrganizationMapper';
import { OrganizationUserMapper } from '../../mappers/organization/OrganizationUserMapper';
import { OrganizationResourcesMapper } from '../../mappers/resources/OrganizationResourcesMapper';
import { SessionRepository } from '../session/SessionRepository';
import { OrganizationRepository } from './OrganizationRepository';

export class OrganizationRepositoryApi implements OrganizationRepository {
  constructor(private requests: Requests, private sessionRepository: SessionRepository) {}

  async register({
    firstName,
    lastName,
    email,
    password,
    phone,
    country,
    language,
    registerUtmInfo,
  }: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    phone: string;
    country?: string;
    language: Language;
    registerUtmInfo: {
      utm_campaign: string;
      utm_content: string;
      utm_source: string;
      utm_term: string;
      utm_medium: string;
      utm_id: string;
      utm_gclid: string;
    };
  }): Promise<Organization> {
    const response = await this.requests.post({
      uri: `/organization`,
      data: {
        first_name: firstName,
        last_name: lastName,
        email,
        password,
        phone,
        country,
        language: language.value,
        ...(registerUtmInfo[`utm_campaign`] !== `` ? { utm_campaign: registerUtmInfo[`utm_campaign`] } : {}),
        ...(registerUtmInfo[`utm_content`] !== `` ? { utm_content: registerUtmInfo[`utm_content`] } : {}),
        ...(registerUtmInfo[`utm_source`] !== `` ? { utm_source: registerUtmInfo[`utm_source`] } : {}),
        ...(registerUtmInfo[`utm_term`] !== `` ? { utm_term: registerUtmInfo[`utm_term`] } : {}),
        ...(registerUtmInfo[`utm_medium`] !== `` ? { utm_medium: registerUtmInfo[`utm_medium`] } : {}),
        ...(registerUtmInfo[`utm_id`] !== `` ? { utm_id: registerUtmInfo[`utm_id`] } : {}),
        ...(registerUtmInfo[`utm_gclid`] !== `` ? { utm_gclid: registerUtmInfo[`utm_gclid`] } : {}),
      },
    });

    const organization = response.data as OrganizationDTO;

    return OrganizationMapper.toDomain(organization);
  }

  async fetch(): Promise<Organization> {
    const response = await this.requests.get({
      uri: `/organization`,
      params: {},
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const organization = response.data as OrganizationDTO;

    return OrganizationMapper.toDomain(organization);
  }

  async save(organization: Organization): Promise<Organization> {
    const response = await this.requests.put({
      uri: `/organization`,
      data: OrganizationMapper.toRepository(organization),
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const updatedOrganization = response.data as OrganizationDTO;

    return OrganizationMapper.toDomain(updatedOrganization);
  }

  async fetchAvatar({ organization }: { organization: Organization }): Promise<Organization> {
    if (!organization.avatar_url) {
      return organization;
    }

    const requests = Requests.create(``);

    const response = await requests.get({
      uri: organization.avatar_url,
      responseType: `arraybuffer`,
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    organization.organization_avatar = await arrayBufferToImageSrcBase64({
      data: response.data,
      dataType: response.headers[`content-type`].toLowerCase(),
    });

    return organization;
  }

  async createAvatar({
    organization,
    fileName,
    mimeType,
    file,
  }: {
    organization: Organization;
    fileName: string;
    mimeType: string;
    file: Blob;
  }): Promise<Organization> {
    const formData = new FormData();
    formData.append(`name`, fileName);
    formData.append(`mime`, mimeType);
    formData.append(`data`, file);

    const response = await this.requests.post({
      uri: `/organization/avatar`,
      data: formData,
      headers: {
        'content-type': `multipart/form-data`,
        ...this.sessionRepository.getAuthenticationHeader(),
      },
    });

    organization.avatar_url = response.data.avatar_url;

    return organization;
  }

  async deleteAvatar({ organization }: { organization: Organization }): Promise<Organization> {
    await this.requests.delete({
      uri: `/organization/avatar`,
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    organization.avatar_url = undefined;
    organization.organization_avatar = undefined;
    return organization;
  }

  async retrieveUsers(): Promise<OrganizationUser[]> {
    const response = await this.requests.get({
      uri: `/organization/users`,
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const users = response.data as OrganizationUserDTO[];

    return users.map((user) => OrganizationUserMapper.toDomain(user));
  }

  async createUser({
    email,
    firstName,
    lastName,
    role,
    language,
    projects,
  }: {
    email: string;
    firstName: string;
    lastName: string;
    role: `organization_administrator` | `organization_manager` | `organization_user`;
    language: Language;
    projects: string[];
  }): Promise<void> {
    await this.requests.post({
      uri: `/organization/invite`,
      data: {
        email: email,
        first_name: firstName,
        last_name: lastName,
        language: language.value,
        role,
        projects,
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });
  }

  async deleteUser({
    organizationUser,
    organization,
  }: {
    organizationUser: OrganizationUser;
    organization: Organization;
  }): Promise<Organization> {
    await this.requests.delete({
      uri: `/organization/users/${organizationUser.id}`,
      headers: this.sessionRepository.getAuthenticationHeader(),
    });
    return organization;
  }

  async editUser({
    organizationUser,
    organization,
  }: {
    organizationUser: OrganizationUser;
    organization: Organization;
  }): Promise<Organization> {
    await this.requests.put({
      uri: `/organization/users/${organizationUser.id}`,
      data: {
        role: organizationUser.role,
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });
    return organization;
  }

  async resendUserInvitation({
    userId,
    organization,
  }: {
    userId: string;
    organization: Organization;
  }): Promise<Organization> {
    await this.requests.put({
      uri: `/organization/invite`,
      data: {
        user_id: userId,
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    return organization;
  }

  async restoreUser({ userId }: { userId: string }): Promise<void> {
    await this.requests.post({
      uri: `/organization/users/${userId}/restore`,
      headers: this.sessionRepository.getAuthenticationHeader(),
    });
  }

  async fetchUserInvitation({ token }: { token: string }): Promise<{
    language: Language;
    email: string;
    firstName: string;
    lastName: string;
    organizationLabel: string;
  }> {
    const response = await this.requests.get({
      uri: `/organization/invitation/${token}`,
    });

    return {
      language: Language.create(response.data.language),
      email: response.data.email,
      firstName: response.data.first_name,
      lastName: response.data.last_name,
      organizationLabel: response.data.organization_label,
    };
  }

  async acceptUserInvitation({
    token,
    language,
    password,
    firstName,
    lastName,
    phone,
  }: {
    token: string;
    language: Language;
    password: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
  }): Promise<void> {
    await this.requests.post({
      uri: `/organization/invitation/${token}`,
      data: {
        language: language.value,
        password,
        first_name: firstName,
        last_name: lastName,
        phone,
      },
    });
  }

  async createPaymentLink({
    redirectUrl,
    plan,
    billingPeriod,
  }: {
    redirectUrl: string;
    plan: PlanType;
    billingPeriod: BillingPeriod;
  }): Promise<string> {
    const response = await this.requests.post({
      uri: `/organization/payment-link`,
      data: {
        redirect_url: redirectUrl,
        plan,
        billing_period: billingPeriod,
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    return response.data.payment_link;
  }

  async retrieveResources(): Promise<OrganizationResources> {
    const response = await this.requests.get({
      uri: `/organization/resources`,
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const resources = response.data as OrganizationResourcesDTO;

    return OrganizationResourcesMapper.toDomain(resources);
  }
}
